import React, { useState, useEffect } from "react";
import styles from "./SidePanel.module.css";
import { FiTrash2 } from "react-icons/fi";
import WarehouseModal from "../customComponentsTable/WarehouseModal";
import CompletedInvoiceModal from "../ComponentsTable/CompletedInvoiceModal";
import ModalProducts from "../../../customComponents/ModalProducts";
import { API_URL } from "../../../config/api";
import axios from "axios";

const SidePanel = ({ warehouse, onClose, onComplete, invoiceId }) => {
  const token = localStorage.getItem("token");

  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [isWarehouseModalOpen, setIsWarehouseModalOpen] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(warehouse);

  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [productsInTable, setProductsInTable] = useState([]);

  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);

  // ✅ Загрузка позиций приемки (items) + загрузка ТОЛЬКО нужных продуктов по ids
  useEffect(() => {
    const fetchStockItems = async () => {
      if (!invoiceId) return;

      try {
        const stockItemsRes = await axios.get(
          `${API_URL}/api/stock_changes/${invoiceId}/items`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const items = stockItemsRes.data?.data || [];

        const ids = Array.from(new Set(items.map((i) => i.product_id).filter(Boolean)));

        if (ids.length === 0) {
          setProductsInTable([]);
          localStorage.setItem(`productsInTable_${invoiceId}`, JSON.stringify([]));
          return;
        }

        const productsRes = await axios.get(`${API_URL}/api/products?ids=${ids.join(",")}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const products = productsRes.data?.data || [];
        const productsById = new Map(products.map((p) => [p.id, p]));

        const enrichedItems = items
          .map((item) => {
            const matchedProduct = productsById.get(item.product_id);
            if (!matchedProduct) return null;

            const purchasePrice = Number(item.purchase_price) || 0;
            const sellPrice = Number(item.sell_price) || 0;
            const quantity = Number(item.quantity) || 0;

            return {
              ...matchedProduct,
              quantity,
              purchase_price: purchasePrice,
              sell_price: sellPrice,

              // это как было — оставил логику (доли, не проценты)
              profit_percentage: purchasePrice > 0 ? (sellPrice - purchasePrice) / purchasePrice : 0,

              // ✅ приемка: "Итого" считаем по закупу
              total: purchasePrice * quantity,

              totalQuantity: quantity,
              unit: "шт",
            };
          })
          .filter(Boolean);

        setProductsInTable(enrichedItems);
        localStorage.setItem(`productsInTable_${invoiceId}`, JSON.stringify(enrichedItems));
      } catch (error) {
        console.error("Ошибка при загрузке товаров для приемки:", error);

        const saved = localStorage.getItem(`productsInTable_${invoiceId}`);
        if (saved) setProductsInTable(JSON.parse(saved));
      }
    };

    fetchStockItems();
  }, [invoiceId, token]);

  // ✅ Остаток со склада (как у тебя было)
  const fetchStockQuantity = async (productId, storageId) => {
    if (!storageId) return 0;
    try {
      const response = await axios.get(`${API_URL}/api/stocks?storage_id=${storageId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const stockData = response.data?.data || [];
      const stockItem = stockData.find((item) => item.product_id === productId);
      return stockItem ? Number(stockItem.quantity) || 0 : 0;
    } catch (error) {
      console.error("Ошибка при получении остатка со склада:", error);
      return 0;
    }
  };

  const handleDelete = (id) => {
    const updated = productsInTable.filter((p) => p.id !== id);
    setProductsInTable(updated);

    if (invoiceId) {
      localStorage.setItem(`productsInTable_${invoiceId}`, JSON.stringify(updated));
    }
  };

  // ✅ Добавление товаров из ModalProducts
  const handleAddProductsFromModal = async (selectedProducts) => {
    if (!selectedProducts || selectedProducts.length === 0) return;

    const updated = [...productsInTable];

    for (const item of selectedProducts) {
      const exists = updated.find((p) => p.id === item.id);

      if (exists) {
        exists.quantity = (Number(exists.quantity) || 0) + 1;
        exists.totalQuantity = exists.quantity;

        // ✅ приемка: total по закупу
        exists.total = (Number(exists.purchase_price) || 0) * exists.quantity;

        exists.profit_percentage =
          (Number(exists.purchase_price) || 0) > 0
            ? ((Number(exists.sell_price) || 0) - (Number(exists.purchase_price) || 0)) /
              (Number(exists.purchase_price) || 0)
            : 0;
      } else {
        const stockQuantity = await fetchStockQuantity(item.id, selectedWarehouse?.id);

        const purchase_price = Number(item.purchase_price || item.price) || 0;
        const sell_price = Number(item.sell_price || item.price) || 0;
        const profit_percentage =
          purchase_price > 0 ? (sell_price - purchase_price) / purchase_price : 0;

        updated.push({
          id: item.id,
          name_ru: item.name,
          ntin: item.barcode,
          quantity: 1,
          totalQuantity: 1,
          unit: "шт",
          purchase_price,
          sell_price,
          profit_percentage,

          // ✅ приемка: total по закупу
          total: purchase_price,

          stockQuantity,
        });
      }
    }

    const finalProducts = updated.map((p) => ({
      ...p,
      profit_percentage:
        (Number(p.purchase_price) || 0) > 0
          ? ((Number(p.sell_price) || 0) - (Number(p.purchase_price) || 0)) /
            (Number(p.purchase_price) || 0)
          : 0,

      // ✅ на всякий — total держим консистентным (закуп)
      total: (Number(p.purchase_price) || 0) * (Number(p.quantity) || 0),
      totalQuantity: Number(p.quantity) || 0,
    }));

    setProductsInTable(finalProducts);

    if (invoiceId) {
      localStorage.setItem(`productsInTable_${invoiceId}`, JSON.stringify(finalProducts));
    }
  };

  // ✅ Подсказки поиска
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchTerm.length < 3) {
        setSuggestions([]);
        return;
      }

      try {
        const response = await axios.get(
          `${API_URL}/api/products?name_ru=${encodeURIComponent(searchTerm)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuggestions(response.data?.data || []);
        setShowSuggestions(true);
      } catch (error) {
        console.error("Ошибка при получении товаров:", error);
      }
    };

    const delayDebounce = setTimeout(() => {
      fetchSuggestions();
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm, token]);

  // ✅ Выбор товара из подсказок
  const handleSelect = async (product) => {
    setSearchTerm("");
    setShowSuggestions(false);

    const alreadyExists = productsInTable.find((p) => p.id === product.id);
    if (alreadyExists) return;

    const stockQuantity = await fetchStockQuantity(product.id, selectedWarehouse?.id);

    const purchase_price = Number(product.purchase_price) || 0;
    const sell_price = Number(product.sell_price) || 0;
    const quantity = 1;

    const newProduct = {
      ...product,
      quantity,
      unit: "шт",
      totalQuantity: quantity,

      // ✅ приемка: total по закупу
      total: purchase_price * quantity,

      stockQuantity,
      purchase_price,
      sell_price,
      profit_percentage: purchase_price > 0 ? (sell_price - purchase_price) / purchase_price : 0,
    };

    const updated = [...productsInTable, newProduct];
    setProductsInTable(updated);

    if (invoiceId) localStorage.setItem(`productsInTable_${invoiceId}`, JSON.stringify(updated));
  };

  // ✅ Изменение полей в таблице
  const handleFieldChange = (id, field, value) => {
    const updated = productsInTable.map((product) => {
      if (product.id === id) {
        let updatedProduct = {
          ...product,
          [field]: ["profit_percentage", "quantity", "purchase_price", "sell_price"].includes(field)
            ? Number(value) || 0
            : value,
        };

        const quantity = Number(updatedProduct.quantity) || 0;
        const purchase_price = Number(updatedProduct.purchase_price) || 0;
        const sell_price = Number(updatedProduct.sell_price) || 0;
        const profit_percentage = Number(updatedProduct.profit_percentage) || 0;

        // profit_percentage у нас в долях (0.2), в инпуте ты показываешь *100
        if (field === "profit_percentage") {
          updatedProduct.sell_price = purchase_price * (1 + profit_percentage);
        }

        if (field === "sell_price") {
          updatedProduct.profit_percentage =
            purchase_price > 0 ? (sell_price - purchase_price) / purchase_price : 0;
        }

        // ✅ приемка: total по закупу
        updatedProduct.total = quantity * purchase_price;
        updatedProduct.totalQuantity = quantity;

        return updatedProduct;
      }
      return product;
    });

    setProductsInTable(updated);

    if (invoiceId) localStorage.setItem(`productsInTable_${invoiceId}`, JSON.stringify(updated));
  };

  // ✅ Отправка приемки
  const handleSubmit = async ({ supplierId, paymentMethodId, shiftId, date }, type = "pay") => {
    if (!warehouse || productsInTable.length === 0) {
      alert("Выберите склад и добавьте хотя бы один товар.");
      return;
    }

    const items = productsInTable.map((product) => ({
      product_id: product.id,
      quantity: Number(product.quantity) || 0,
      purchase_price: Number(product.purchase_price) || 0,
      sell_price: Number(product.sell_price) || 0,

      // как у тебя было — amount/доход/прибыль считаются по продаже
      amount: (Number(product.sell_price) || 0) * (Number(product.quantity) || 0),
      p_income: (Number(product.sell_price) || 0) * (Number(product.quantity) || 0),
      p_profit:
        ((Number(product.sell_price) || 0) - (Number(product.purchase_price) || 0)) *
        (Number(product.quantity) || 0),
    }));

    const payload = {
      storage_id: selectedWarehouse.id,
      type: 1,
      quantity: items.reduce((sum, p) => sum + (Number(p.quantity) || 0), 0),
      status: "approved",
      amount: items.reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
      p_income: items.reduce((sum, p) => sum + (Number(p.p_income) || 0), 0),
      p_profit: items.reduce((sum, p) => sum + (Number(p.p_profit) || 0), 0),
      wallet_id: paymentMethodId,
      items,
      supplier_id: supplierId,
      shift_id: shiftId,
      date,
    };

    try {
      await axios.post(`${API_URL}/api/stock_changes`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setProductsInTable([]);
      if (onComplete) onComplete();
      onClose();
      if (invoiceId) localStorage.removeItem(`productsInTable_${invoiceId}`);
    } catch (error) {
      console.error("Ошибка при отправке:", error);
      alert("Ошибка при отправке приемки.");
    }
  };

  // ✅ Карточки сверху: закуп / количество / доход / прибыль
  const totalPurchaseAmount = productsInTable.reduce(
    (sum, p) => sum + (Number(p.purchase_price) || 0) * (Number(p.quantity) || 0),
    0
  );

  const totalQuantity = productsInTable.reduce((sum, p) => sum + (Number(p.quantity) || 0), 0);

  const totalIncome = productsInTable.reduce(
    (sum, p) => sum + (Number(p.sell_price) || 0) * (Number(p.quantity) || 0),
    0
  );

  const totalProfit = totalIncome - totalPurchaseAmount;

  return (
    <div className={styles.sidePanel}>
      <div className={styles.header}>
        <button
          className={styles.backButton}
          onClick={() => {
            setProductsInTable([]);
            onClose();
          }}
        >
          <p className={styles.backText}>Приемка</p>
        </button>
      </div>

      <div className={styles.content}>
        <div className={styles.firstBlock}>
          <p className={styles.text}>Склад</p>
          <h2 className={styles.title}>{selectedWarehouse?.name}</h2>
          <button onClick={() => setIsWarehouseModalOpen(true)} className={styles.change}>
            Изменить
          </button>
        </div>

        <div className={styles.secondBlock}>
          <p className={styles.title}>Итого к оплате</p>
          <p className={styles.price}>{totalPurchaseAmount.toLocaleString()} ₸</p>
        </div>

        <div className={styles.thirdBlock}>
          <p className={styles.title}>Итого количество</p>
          <p className={styles.price}>{totalQuantity}</p>
        </div>

        <div className={styles.fourthBlock}>
          <p className={styles.title}>Возможный доход</p>
          <p className={styles.price}>{totalIncome.toLocaleString()} ₸</p>
        </div>

        <div className={styles.fifthBlock}>
          <p className={styles.title}>Возможная прибыль</p>
          <p className={styles.price}>{totalProfit.toLocaleString()} ₸</p>
        </div>
      </div>

      <div className={styles.searchBlock}>
        <input
          type="search"
          name="search"
          className={styles.search}
          placeholder="Поиск товара по названию или по штрихкоду"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        />

        {showSuggestions && suggestions.length > 0 && (
          <ul className={styles.suggestionList}>
            {suggestions.map((item) => (
              <li
                key={item.id}
                className={styles.suggestionItem}
                onClick={() => handleSelect(item)}
              >
                {item.name_ru}
              </li>
            ))}
          </ul>
        )}

        <div className={styles.addProductWrap}>
          <button className={styles.addProduct} onClick={() => setShowAddDropdown((prev) => !prev)}>
            Добавить товар
          </button>

          {showAddDropdown && (
            <div className={styles.dropdownMenu}>
              <div
                className={styles.dropdownItem}
                onClick={() => {
                  setShowAddDropdown(false);
                  setIsProductModalOpen(true);
                }}
              >
                <b>Из номенклатуры</b>
                <p>Выбрать определённые товары из списка</p>
              </div>

              <div className={styles.dropdownItem}>
                <b>Из склада</b>
                <p>Добавить все товары из выбранного склада</p>
              </div>

              <div className={styles.dropdownItem}>
                <b>Товары поставщика</b>
                <p>Добавить все товары прикрепленные к поставщику</p>
              </div>

              <div className={styles.dropdownItem}>
                <b>Создать новый товар</b>
                <p>Создать и добавить новый товар</p>
              </div>
            </div>
          )}
        </div>

        <button className={styles.endProduct} onClick={() => setIsCompleteModalOpen(true)}>
          Завершить приемку
        </button>
      </div>

      {productsInTable.length > 0 && (
        <table className={styles.productsTable}>
          <thead>
            <tr>
              <th>Название</th>
              <th>Штрихкод</th>
              <th>Кол-во</th>
              <th>Ед. изм.</th>
              <th>Цена закупки</th>
              <th>Накрутка %</th>
              <th>Цена продажи</th>
              <th>Остаток</th>
              <th>Итого кол-во</th>
              <th>Итого</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {productsInTable.map((product) => (
              <tr key={product.id}>
                <td>{product.name_ru}</td>
                <td>{product.ntin}</td>

                <td>
                  <input
                    type="number"
                    min="0"
                    className={styles.changeNum}
                    value={product.quantity}
                    onChange={(e) => handleFieldChange(product.id, "quantity", e.target.value)}
                    style={{ width: "60px" }}
                  />
                </td>

                <td>{product.unit}</td>

                <td>
                  <input
                    type="number"
                    step="0.01"
                    className={styles.changeNum}
                    value={Number(product.purchase_price)}
                    onChange={(e) => handleFieldChange(product.id, "purchase_price", e.target.value)}
                    style={{ width: "80px" }}
                  />
                  ₸
                </td>

                <td>
                  <input
                    type="number"
                    step="0.01"
                    className={styles.changeNum}
                    value={(Number(product.profit_percentage) * 100).toFixed(2)}
                    onChange={(e) =>
                      handleFieldChange(
                        product.id,
                        "profit_percentage",
                        Number(e.target.value) / 100
                      )
                    }
                    style={{ width: "60px" }}
                  />
                  %
                </td>

                <td>
                  <input
                    type="number"
                    step="0.01"
                    className={styles.changeNum}
                    value={Number(product.sell_price)}
                    onChange={(e) => handleFieldChange(product.id, "sell_price", e.target.value)}
                    style={{ width: "80px" }}
                  />
                  ₸
                </td>

                <td>{product.stockQuantity || 0}</td>
                <td>{product.totalQuantity}</td>

                {/* ✅ Итого по закупу (приемка) */}
                <td>{Number(product.total || 0).toLocaleString()} ₸</td>

                <td>
                  <button
                    onClick={() => handleDelete(product.id)}
                    style={{
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      color: "red",
                      fontSize: "18px",
                    }}
                  >
                    <FiTrash2 />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <WarehouseModal
        isOpen={isWarehouseModalOpen}
        onClose={() => setIsWarehouseModalOpen(false)}
        onSelect={(wh) => {
          setSelectedWarehouse(wh);
        }}
      />

      <ModalProducts
        open={isProductModalOpen}
        onClose={() => setIsProductModalOpen(false)}
        warehouseId={selectedWarehouse?.id}
        onAddProducts={handleAddProductsFromModal}
        stocks={false}
      />

      <CompletedInvoiceModal
        open={isCompleteModalOpen}
        onClose={() => setIsCompleteModalOpen(false)}
        warehouseId={selectedWarehouse?.id}
        onPay={(data) => handleSubmit(data, "pay")}
        onPayLater={(data) => handleSubmit(data, "later")}
      />
    </div>
  );
};

export default SidePanel;
